import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationContextType {
  history: string[];
  canGoBack: boolean;
  goBack: () => void;
  resetHistory: () => void;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    setHistory(prev => {
      const current = location.pathname;
      // Don't add duplicates
      if (prev[prev.length - 1] === current) return prev;
      return [...prev, current];
    });
  }, [location.pathname]);

  const canGoBack = history.length > 1;

  const goBack = useCallback(() => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop(); // Remove current
      const prevPath = newHistory[newHistory.length - 1];
      setHistory(newHistory);
      navigate(prevPath);
    } else {
      navigate('/');
    }
  }, [history, navigate]);

  const resetHistory = useCallback(() => {
    setHistory(['/']);
    navigate('/');
  }, [navigate]);

  return (
    <NavigationContext.Provider value={{ history, canGoBack, goBack, resetHistory }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
}
